import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, storage } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import {
  collection, query, where, onSnapshot, addDoc, deleteDoc, doc, serverTimestamp
} from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import {
  Box, Container, Typography, Paper, Button, TextField, CircularProgress,
  IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import WallpaperIcon from '@mui/icons-material/Wallpaper';
import LagbasLogo from '../components/LagbasLogo';

function UploadDialog({ open, onClose, onSave, companyId, uid }) {
  const [name, setName]         = useState('');
  const [file, setFile]         = useState(null);
  const [preview, setPreview]   = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError]       = useState('');

  function pickFile(e) {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    if (!name) setName(f.name.replace(/\.[^.]+$/, ''));
  }

  async function handleSave() {
    if (!file || !name.trim()) return;
    setUploading(true);
    setError('');
    try {
      const path = `castBackgrounds/${companyId}/${Date.now()}_${file.name}`;
      const snap = await uploadBytes(storageRef(storage, path), file);
      const url  = await getDownloadURL(snap.ref);
      await addDoc(collection(db, 'castBackgrounds'), {
        name: name.trim(), url, storagePath: path,
        companyId, createdBy: uid, createdAt: serverTimestamp(),
      });
      setName(''); setFile(null); setPreview(null);
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setUploading(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Lägg till bakgrundsbild</DialogTitle>
      <DialogContent sx={{ display:'flex', flexDirection:'column', gap: 2, pt: 2 }}>
        {error && <Typography color="error" variant="body2">{error}</Typography>}

        {/* Preview */}
        <Box
          sx={{ height: 180, borderRadius: 2, overflow:'hidden', bgcolor:'#1a1a1a', border:'2px dashed #444',
            display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', position:'relative' }}
          onClick={() => document.getElementById('bg-file-input').click()}>
          {preview
            ? <img src={preview} alt="preview" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
            : <Box sx={{ textAlign:'center', color:'#666' }}>
                <UploadFileIcon sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="body2">Klicka för att välja bild</Typography>
                <Typography variant="caption">JPG, PNG, WebP – max 10 MB</Typography>
              </Box>
          }
          <input id="bg-file-input" type="file" accept=".jpg,.jpeg,.png,.webp"
            style={{ display:'none' }} onChange={pickFile} />
        </Box>

        <TextField autoFocus fullWidth label="Namn" value={name}
          onChange={e => setName(e.target.value)}
          helperText="Visas i bakgrundsväljaren när du skapar anslag" />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Avbryt</Button>
        <Button variant="contained" disabled={!file || !name.trim() || uploading}
          sx={{ bgcolor:'#FF6B00', '&:hover':{ bgcolor:'#e55a00' } }}
          onClick={handleSave}>
          {uploading ? <CircularProgress size={20} sx={{ color:'white' }} /> : 'Ladda upp'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function CastNBackgrounds() {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const companyId = userProfile?.companyId || '';
  const uid       = currentUser?.uid || '';

  const [backgrounds, setBackgrounds] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [uploadOpen, setUploadOpen]   = useState(false);
  const [deleting, setDeleting]       = useState(null);

  useEffect(() => {
    if (!companyId) return;
    const q = query(collection(db, 'castBackgrounds'), where('companyId', '==', companyId));
    return onSnapshot(q, snap => {
      setBackgrounds(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
  }, [companyId]);

  async function handleDelete(bg) {
    if (!window.confirm(`Ta bort "${bg.name}"?`)) return;
    setDeleting(bg.id);
    try {
      if (bg.storagePath) {
        await deleteObject(storageRef(storage, bg.storagePath)).catch(() => {});
      }
      await deleteDoc(doc(db, 'castBackgrounds', bg.id));
    } catch (e) { console.error(e); }
    finally { setDeleting(null); }
  }

  return (
    <Box sx={{ minHeight:'100vh', bgcolor:'#f5f5f5' }}>
      {/* Header */}
      <Box sx={{ bgcolor:'#1a1a1a', color:'white', px: 3, py: 2, display:'flex', alignItems:'center', gap: 2 }}>
        <LagbasLogo size={32} />
        <Typography variant="h6" fontWeight="bold">
          Cast<span style={{ color:'#FF6B00' }}>N</span>
          <span style={{ color:'#aaa', fontWeight:400, fontSize:'0.9rem', marginLeft:8 }}>– Bakgrundsbilder</span>
        </Typography>
        <Chip label={`${backgrounds.length} bilder`} size="small"
          sx={{ bgcolor:'#FF6B0030', color:'#FF6B00', fontWeight:'bold', ml: 1 }} />
        <Box sx={{ flex: 1 }} />
        <Button startIcon={<ArrowBackIcon />} sx={{ color:'#aaa' }}
          onClick={() => navigate('/marketing/castn')}>
          Tillbaka
        </Button>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Info */}
        <Paper sx={{ p: 3, mb: 3, bgcolor:'#fff9f5', border:'1px solid #ffe0cc' }}>
          <Typography variant="body2" sx={{ color:'#666' }}>
            <strong>Bakgrundsbilder</strong> är tillgängliga för alla som skapar anslag i CastN. De fungerar som
            en delad bildbank för hela organisationen — välj en bakgrund på ett Cast och alla skärmar som spelar
            det Casten visar bilden bakom zonerna. Bäst resultat ger bilder i <strong>16:9-format</strong> (1920×1080 px eller större).
          </Typography>
        </Paper>

        {/* Action bar */}
        <Box sx={{ display:'flex', justifyContent:'flex-end', mb: 3 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setUploadOpen(true)}
            sx={{ bgcolor:'#FF6B00', '&:hover':{ bgcolor:'#e55a00' } }}>
            Lägg till bakgrund
          </Button>
        </Box>

        {loading && <Box sx={{ textAlign:'center', py: 8 }}><CircularProgress sx={{ color:'#FF6B00' }} /></Box>}

        {!loading && backgrounds.length === 0 && (
          <Paper sx={{ p: 6, textAlign:'center' }}>
            <WallpaperIcon sx={{ fontSize: 60, color:'#ddd', mb: 2 }} />
            <Typography variant="h6" sx={{ color:'#aaa', mb: 1 }}>Inga bakgrundsbilder ännu</Typography>
            <Typography variant="body2" sx={{ color:'#bbb', mb: 3 }}>
              Ladda upp bilder i 16:9-format (1920×1080 px) för bäst resultat.
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setUploadOpen(true)}
              sx={{ bgcolor:'#FF6B00', '&:hover':{ bgcolor:'#e55a00' } }}>
              Lägg till bakgrund
            </Button>
          </Paper>
        )}

        {/* Grid */}
        <Box sx={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(280px, 1fr))', gap: 2.5 }}>
          {backgrounds.map(bg => (
            <Paper key={bg.id} sx={{ overflow:'hidden', borderRadius: 2, '&:hover .delete-btn':{ opacity: 1 } }}>
              {/* Image */}
              <Box sx={{ position:'relative', aspectRatio:'16/9', bgcolor:'#1a1a1a', overflow:'hidden' }}>
                <img src={bg.url} alt={bg.name}
                  style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} />
                <Tooltip title="Ta bort">
                  <IconButton
                    className="delete-btn"
                    disabled={deleting === bg.id}
                    onClick={() => handleDelete(bg)}
                    sx={{ position:'absolute', top: 8, right: 8, bgcolor:'rgba(0,0,0,0.6)',
                      color:'white', opacity: 0, transition:'opacity 0.2s',
                      '&:hover':{ bgcolor:'rgba(200,0,0,0.8)' } }}>
                    {deleting === bg.id
                      ? <CircularProgress size={18} sx={{ color:'white' }} />
                      : <DeleteIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
              </Box>
              {/* Name */}
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography fontWeight="bold" noWrap>{bg.name}</Typography>
                <Typography variant="caption" sx={{ color:'#aaa' }}>
                  {bg.createdAt?.toDate?.()?.toLocaleDateString('sv-SE') || ''}
                </Typography>
              </Box>
            </Paper>
          ))}
        </Box>
      </Container>

      <UploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onSave={() => {}}
        companyId={companyId}
        uid={uid}
      />
    </Box>
  );
}
